import { clsx } from 'clsx';

import { getAccessibilityProps } from '../../../internal/utils/getAccessibilityProps';
import type { AccessibilityProps } from '../../../internal/utils/getAccessibilityProps';
import type { ColorKey } from '../../../theme/themeColors.types';
import type { ReeferBaseProps } from '../../../utils/baseProps.types';
import {
  getColorCSSVar,
  getColorForBackgroundCSSVar,
} from '../../../utils/colors';
import { toCSSSize } from '../../../utils/css';
import { fakeButtonProps, handleEnterKey } from '../../../utils/handleEnterKey';
import { paddingCSSVars } from '../../../utils/spacing';
import type { PaddingProperties } from '../../../utils/spacing.types';
import type { CardBaseProps } from '../card';
import styles from './cardContent.module.css';

export interface CardContentProps
  extends
    AccessibilityProps,
    PaddingProperties,
    CardBaseProps,
    ReeferBaseProps {
  /** Add a background color to the component */
  background?: ColorKey;
}

/**
 * In general, the `Card.Content` component should always be included as a child of a `Card` component.
 * The main contents of the card are added as children of this component.
 */

export function CardContent({
  ariaLabel,
  ariaLabelledBy,
  background = 'grays-white',
  border = 'none',
  borderWidth = '1px',
  children,
  className,
  id,
  onClick,
  role,
  'data-testid': testId,
  height = '100%',
  width = '100%',
  p = 16,
  px,
  py,
  pt,
  pr,
  pb,
  pl,
}: CardContentProps) {
  const a11yProps = getAccessibilityProps({
    ariaLabel,
    ariaLabelledBy,
    role,
    onClick,
  });

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={clsx(className, styles['cardContent'], {
        [styles['cardContent__clickable']]: !!onClick,
      })}
      id={id}
      style={{
        '--cardContent-background-color': getColorCSSVar(background),
        '--cardContent-border':
          border === 'none' ? 'none' : `solid ${getColorCSSVar(border)}`,
        '--cardContent-border-width': border === 'none' ? 0 : borderWidth,
        '--cardContent-color': getColorForBackgroundCSSVar(background),
        '--cardContent-height': toCSSSize(height),
        '--cardContent-width': toCSSSize(width),
        ...paddingCSSVars({ p, px, py, pt, pr, pb, pl }),
      }}
      data-testid={testId}
      onClick={onClick}
      onKeyDown={(event) => handleEnterKey(event, onClick)}
      {...(onClick && fakeButtonProps)}
      {...a11yProps}
    >
      {children}
    </div>
  );
}
